import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import ClientCampaignTrigger from '@/components/ClientCampaignTrigger';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Send, Plus, Play, Pause, Eye, RefreshCw, MoreHorizontal, X, Info, Users, Clock, CheckCircle, AlertCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/lib/api';

interface Campaign {
  id: number;
  name: string;
  description: string;
  status: string;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  integration_config?: {
    endpoint?: string;
  };
}

interface CampaignContact {
  id: number;
  campaign_id: number;
  phone_number: string;
  name?: string;
  custom_variables: any;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  failed_at?: string;
  error_message?: string;
  status: 'PENDENTE' | 'PROCESSANDO' | 'ENVIADO' | 'FALHA';
  created_at: string;
}

const ClientTriggers = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignContacts, setCampaignContacts] = useState<CampaignContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/campaigns') as any;
      
      console.log('📊 Response completa:', response);
      console.log('📊 Response.data:', response?.data);
      
      // O backend retorna: { success: true, campaigns: [...], total, currentPage, totalPages }
      const campaignsData = response?.data?.campaigns || response?.campaigns || [];
      
      console.log('📊 Campaigns data:', campaignsData);
      
      if (Array.isArray(campaignsData)) {
        setCampaigns(campaignsData.filter((campaign: Campaign) => 
          campaign.status !== 'rascunho' // Só mostrar campanhas ativas
        ));
        console.log('✅ Campanhas carregadas:', campaignsData.length);
      } else {
        console.error('❌ Dados de campanhas não são um array:', campaignsData);
        console.error('❌ Response completa:', response);
        setCampaigns([]);
        toast({
          title: "Formato de dados inválido",
          description: "Os dados de campanhas retornados não estão no formato esperado",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('❌ Erro ao carregar campanhas:', error);
      setCampaigns([]);
      toast({
        title: "Erro ao carregar campanhas",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const handleCancelCampaign = async (campaignId: number) => {
    try {
      setActionLoading(`cancel-${campaignId}`);
      await apiService.post(`/campaigns/${campaignId}/cancel`);
      
      toast({
        title: "Campanha cancelada",
        description: "A campanha foi cancelada com sucesso."
      });
      
      loadCampaigns();
    } catch (error: any) {
      toast({
        title: "Erro ao cancelar campanha",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseCampaign = async (campaignId: number) => {
    try {
      setActionLoading(`pause-${campaignId}`);
      await apiService.post(`/campaigns/${campaignId}/pause`);
      
      toast({
        title: "Campanha pausada",
        description: "A campanha foi pausada com sucesso."
      });
      
      loadCampaigns();
    } catch (error: any) {
      toast({
        title: "Erro ao pausar campanha",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeCampaign = async (campaignId: number) => {
    try {
      setActionLoading(`resume-${campaignId}`);
      // Tenta a rota padrão de campanhas; se não existir no backend atual (VPS), faz fallback para rota de fila
      try {
        await apiService.post(`/campaigns/${campaignId}/resume`);
      } catch (primaryError) {
        // Fallback para rota existente em produção (queue)
        await apiService.post(`/queue/campaigns/${campaignId}/resume`);
      }
      
      toast({
        title: "Campanha retomada",
        description: "A campanha foi retomada com sucesso."
      });
      
      loadCampaigns();
    } catch (error: any) {
      toast({
        title: "Erro ao retomar campanha",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const loadCampaignContacts = async (campaignId: number) => {
    try {
      setLoadingContacts(true);
      const response = await apiService.get(`/campaigns/${campaignId}/contacts`) as any;
      const contacts = response?.contacts || [];
      setCampaignContacts(contacts);
    } catch (error: any) {
      console.error('❌ Erro ao carregar contatos:', error);
      toast({
        title: "Erro ao carregar contatos",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
    loadCampaignContacts(campaign.id);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'em_andamento': { label: 'Em Andamento', variant: 'default' as const },
      'ativa': { label: 'Ativa', variant: 'default' as const },
      'pausada': { label: 'Pausada', variant: 'secondary' as const },
      'concluida': { label: 'Concluída', variant: 'outline' as const },
      'concluido': { label: 'Concluído', variant: 'outline' as const },
      'erro': { label: 'Erro', variant: 'destructive' as const },
      'aguardando': { label: 'Aguardando', variant: 'secondary' as const }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || 
                   { label: status, variant: 'outline' as const };

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const getEndpointBadge = (campaign: Campaign) => {
    const endpoint = campaign.integration_config?.endpoint;
    
    if (endpoint === '/sendurl') {
      return (
        <Badge variant="default" className="bg-blue-100 text-blue-800">
          📎 Com Mídia
        </Badge>
      );
    } else if (endpoint === '/sendtext') {
      return (
        <Badge variant="outline">
          💬 Apenas Texto
        </Badge>
      );
    }
    
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  const getProgress = (campaign: Campaign) => {
    if (campaign.total_contacts === 0) return 0;
    return Math.round((campaign.sent_count / campaign.total_contacts) * 100);
  };

  const getContactStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDENTE': { label: 'Pendente', variant: 'secondary' as const, icon: Clock },
      'PROCESSANDO': { label: 'Processando', variant: 'default' as const, icon: RefreshCw },
      'ENVIADO': { label: 'Enviado', variant: 'outline' as const, icon: CheckCircle },
      'FALHA': { label: 'Falha', variant: 'destructive' as const, icon: AlertCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || 
                   { label: status, variant: 'outline' as const, icon: Info };

    const IconComponent = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <IconComponent className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getContactStats = () => {
    const stats = {
      total: campaignContacts.length,
      pending: campaignContacts.filter(c => c.status === 'PENDENTE').length,
      processing: campaignContacts.filter(c => c.status === 'PROCESSANDO').length,
      sent: campaignContacts.filter(c => c.status === 'ENVIADO').length,
      failed: campaignContacts.filter(c => c.status === 'FALHA').length
    };
    return stats;
  };

  const exportToExcel = () => {
    if (!selectedCampaign || campaignContacts.length === 0) {
      toast({
        title: "Nenhum dado para exportar",
        description: "Carregue os contatos da campanha primeiro",
        variant: "destructive"
      });
      return;
    }

    try {
      // Preparar dados para exportação
      const exportData = campaignContacts.map(contact => ({
        'Nome': contact.name || 'Sem nome',
        'Telefone': contact.phone_number,
        'Status': contact.status,
        'Enviado em': contact.sent_at ? formatDate(contact.sent_at) : '-',
        'Erro': contact.error_message || '-',
        'Criado em': formatDate(contact.created_at)
      }));

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Log de Envio');

      // Gerar nome do arquivo
      const fileName = `log_envio_${selectedCampaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Salvar arquivo
      XLSX.writeFile(wb, fileName);

      toast({
        title: "Exportação realizada",
        description: `Log de envio exportado como ${fileName}`,
      });
    } catch (error) {
      console.error('❌ Erro ao exportar:', error);
      toast({
        title: "Erro na exportação",
        description: "Não foi possível exportar o arquivo",
        variant: "destructive"
      });
    }
  };

  const exportToCSV = () => {
    if (!selectedCampaign || campaignContacts.length === 0) {
      toast({
        title: "Nenhum dado para exportar",
        description: "Carregue os contatos da campanha primeiro",
        variant: "destructive"
      });
      return;
    }

    try {
      // Preparar dados para exportação
      const exportData = campaignContacts.map(contact => ({
        'Nome': contact.name || 'Sem nome',
        'Telefone': contact.phone_number,
        'Status': contact.status,
        'Enviado em': contact.sent_at ? formatDate(contact.sent_at) : '-',
        'Erro': contact.error_message || '-',
        'Criado em': formatDate(contact.created_at)
      }));

      // Converter para CSV
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escapar vírgulas e aspas no CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Criar blob e download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `log_envio_${selectedCampaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Exportação realizada",
        description: `Log de envio exportado como CSV`,
      });
    } catch (error) {
      console.error('❌ Erro ao exportar CSV:', error);
      toast({
        title: "Erro na exportação",
        description: "Não foi possível exportar o arquivo CSV",
        variant: "destructive"
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Disparos de Campanha</h1>
            <p className="text-muted-foreground">
              Gerencie e dispare campanhas de WhatsApp
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadCampaigns}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              onClick={() => setShowTriggerModal(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nova Campanha
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total de Campanhas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{campaigns.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Campanhas Ativas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {campaigns.filter(c => c.status === 'ativa').length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Enviado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {campaigns.reduce((sum, c) => sum + c.sent_count, 0)}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total de Falhas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {campaigns.reduce((sum, c) => sum + c.failed_count, 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Suas Campanhas
            </CardTitle>
            <CardDescription>
              Histórico e status das suas campanhas de disparo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-8">
                <Send className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhuma campanha encontrada</h3>
                <p className="text-muted-foreground mb-4">
                  Crie sua primeira campanha para começar a disparar mensagens
                </p>
                <Button onClick={() => setShowTriggerModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeira Campanha
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Progresso e Controles</TableHead>
                    <TableHead>Enviados</TableHead>
                    <TableHead>Falhas</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{campaign.name}</div>
                          {campaign.description && (
                            <div className="text-sm text-muted-foreground">
                              {campaign.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell>{getEndpointBadge(campaign)}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          {/* Barra de Progresso */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <div className="text-sm font-medium">
                                {getProgress(campaign)}%
                              </div>
                              <div className="text-xs text-gray-500">
                                ({campaign.sent_count}/{campaign.total_contacts})
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${getProgress(campaign)}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Botões de Controle */}
                          <div className="flex items-center space-x-1">
                            {(campaign.status === 'ativa' || campaign.status === 'em_andamento') && (
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => handlePauseCampaign(campaign.id)}
                                disabled={actionLoading === `pause-${campaign.id}`}
                                className="h-8 px-2"
                              >
                                <Pause className="h-3 w-3" />
                              </Button>
                            )}
                            {campaign.status === 'pausada' && (
                              <Button 
                                variant="default" 
                                size="sm" 
                                onClick={() => handleResumeCampaign(campaign.id)}
                                disabled={actionLoading === `resume-${campaign.id}`}
                                className="h-8 px-2"
                              >
                                <Play className="h-3 w-3" />
                              </Button>
                            )}
                            {(campaign.status === 'ativa' || campaign.status === 'em_andamento' || campaign.status === 'pausada' || campaign.status === 'agendada') && (
                              <Button 
                                variant="destructive" 
                                size="sm" 
                                onClick={() => handleCancelCampaign(campaign.id)}
                                disabled={actionLoading === `cancel-${campaign.id}`}
                                className="h-8 px-2"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-600 font-medium">
                          {campaign.sent_count}
                        </span>
                        <span className="text-muted-foreground">
                          /{campaign.total_contacts}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-red-600 font-medium">
                          {campaign.failed_count}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(campaign.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetails(campaign)}>
                              <Info className="mr-2 h-4 w-4" />
                              Ver Detalhes
                            </DropdownMenuItem>
                            
                            {(campaign.status === 'ativa' || campaign.status === 'em_andamento') && (
                              <DropdownMenuItem 
                                onClick={() => handlePauseCampaign(campaign.id)}
                                disabled={actionLoading === `pause-${campaign.id}`}
                              >
                                <Pause className="mr-2 h-4 w-4" />
                                {actionLoading === `pause-${campaign.id}` ? 'Pausando...' : 'Pausar'}
                              </DropdownMenuItem>
                            )}
                            
                            {campaign.status === 'pausada' && (
                              <DropdownMenuItem 
                                onClick={() => handleResumeCampaign(campaign.id)}
                                disabled={actionLoading === `resume-${campaign.id}`}
                              >
                                <Play className="mr-2 h-4 w-4" />
                                {actionLoading === `resume-${campaign.id}` ? 'Retomando...' : 'Retomar'}
                              </DropdownMenuItem>
                            )}
                            
                            {(campaign.status === 'ativa' || campaign.status === 'em_andamento' || campaign.status === 'pausada' || campaign.status === 'agendada') && (
                              <DropdownMenuItem 
                                onClick={() => handleCancelCampaign(campaign.id)}
                                disabled={actionLoading === `cancel-${campaign.id}`}
                                className="text-red-600"
                              >
                                <X className="mr-2 h-4 w-4" />
                                {actionLoading === `cancel-${campaign.id}` ? 'Cancelando...' : 'Cancelar'}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Campaign Trigger Modal */}
        <ClientCampaignTrigger
          open={showTriggerModal}
          onClose={() => setShowTriggerModal(false)}
          onSuccess={loadCampaigns}
        />

        {/* Campaign Details Modal */}
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Detalhes da Campanha
              </DialogTitle>
              <DialogDescription>
                Informações detalhadas, estatísticas e log de envio da campanha
              </DialogDescription>
            </DialogHeader>

            {selectedCampaign && (
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                  <TabsTrigger value="contacts" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Log de Envio ({campaignContacts.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  {/* Informações Básicas */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Nome</Label>
                      <p className="text-sm text-muted-foreground">{selectedCampaign.name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Status</Label>
                      <div className="mt-1">{getStatusBadge(selectedCampaign.status)}</div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Tipo</Label>
                      <div className="mt-1">{getEndpointBadge(selectedCampaign)}</div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Criado em</Label>
                      <p className="text-sm text-muted-foreground">{formatDate(selectedCampaign.created_at)}</p>
                    </div>
                  </div>

                  {/* Estatísticas */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Estatísticas de Envio</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-blue-600">{selectedCampaign.total_contacts}</div>
                          <p className="text-xs text-muted-foreground">Total de Contatos</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-green-600">{selectedCampaign.sent_count}</div>
                          <p className="text-xs text-muted-foreground">Enviados</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-red-600">{selectedCampaign.failed_count}</div>
                          <p className="text-xs text-muted-foreground">Falhas</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Progresso */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Progresso</Label>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-blue-600 h-4 rounded-full flex items-center justify-center"
                        style={{ width: `${getProgress(selectedCampaign)}%` }}
                      >
                        <span className="text-xs text-white font-medium">
                          {getProgress(selectedCampaign)}%
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedCampaign.sent_count} de {selectedCampaign.total_contacts} mensagens enviadas
                    </p>
                  </div>

                  {/* Descrição */}
                  {selectedCampaign.description && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Descrição</Label>
                      <p className="text-sm text-muted-foreground">{selectedCampaign.description}</p>
                    </div>
                  )}

                  {/* Botões de Ação */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                      Fechar
                    </Button>
                    
                    {(selectedCampaign.status === 'ativa' || selectedCampaign.status === 'em_andamento') && (
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          handlePauseCampaign(selectedCampaign.id);
                          setShowDetailsModal(false);
                        }}
                        disabled={actionLoading === `pause-${selectedCampaign.id}`}
                      >
                        <Pause className="mr-2 h-4 w-4" />
                        Pausar Campanha
                      </Button>
                    )}
                    
                    {selectedCampaign.status === 'pausada' && (
                      <Button 
                        onClick={() => {
                          handleResumeCampaign(selectedCampaign.id);
                          setShowDetailsModal(false);
                        }}
                        disabled={actionLoading === `resume-${selectedCampaign.id}`}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Retomar Campanha
                      </Button>
                    )}
                    
                    {(selectedCampaign.status === 'ativa' || selectedCampaign.status === 'em_andamento' || selectedCampaign.status === 'pausada' || selectedCampaign.status === 'agendada') && (
                      <Button 
                        variant="destructive"
                        onClick={() => {
                          handleCancelCampaign(selectedCampaign.id);
                          setShowDetailsModal(false);
                        }}
                        disabled={actionLoading === `cancel-${selectedCampaign.id}`}
                      >
                        <X className="mr-2 h-4 w-4" />
                        Cancelar Campanha
                      </Button>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="contacts" className="space-y-6">
                  {/* Estatísticas dos Contatos */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Status dos Contatos</Label>
                    <div className="grid grid-cols-5 gap-4">
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-blue-600">{getContactStats().total}</div>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-yellow-600">{getContactStats().pending}</div>
                          <p className="text-xs text-muted-foreground">Pendentes</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-orange-600">{getContactStats().processing}</div>
                          <p className="text-xs text-muted-foreground">Processando</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-green-600">{getContactStats().sent}</div>
                          <p className="text-xs text-muted-foreground">Enviados</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-2xl font-bold text-red-600">{getContactStats().failed}</div>
                          <p className="text-xs text-muted-foreground">Falhas</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Tabela de Contatos */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <Label className="text-sm font-medium">Lista de Contatos</Label>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadCampaignContacts(selectedCampaign.id)}
                          disabled={loadingContacts}
                        >
                          <RefreshCw className={`h-4 w-4 mr-2 ${loadingContacts ? 'animate-spin' : ''}`} />
                          Atualizar
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Download className="h-4 w-4 mr-2" />
                              Exportar
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={exportToExcel}>
                              📊 Exportar Excel (.xlsx)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={exportToCSV}>
                              📄 Exportar CSV (.csv)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    {loadingContacts ? (
                      <div className="flex justify-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin" />
                      </div>
                    ) : campaignContacts.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Nenhum contato encontrado</h3>
                        <p className="text-muted-foreground">
                          Os contatos desta campanha ainda não foram carregados
                        </p>
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead>Telefone</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Enviado em</TableHead>
                              <TableHead>Erro</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {campaignContacts.map((contact) => (
                              <TableRow key={contact.id}>
                                <TableCell>
                                  <div className="font-medium">
                                    {contact.name || 'Sem nome'}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="font-mono text-sm">
                                    {contact.phone_number}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {getContactStatusBadge(contact.status)}
                                </TableCell>
                                <TableCell>
                                  {contact.sent_at ? (
                                    <span className="text-sm">
                                      {formatDate(contact.sent_at)}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">
                                      -
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {contact.error_message ? (
                                    <div className="max-w-xs">
                                      <span className="text-sm text-red-600">
                                        {contact.error_message}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">
                                      -
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientTriggers;
